import { useState, useCallback } from "react";
import { RadiologistCutoff } from "./useRadiologistCutoffs";

// value encoding:
//   "available"   → type: "available",   cutoffMinutesFromMidnight: null
//   "unavailable" → type: "unavailable", cutoffMinutesFromMidnight: null
//   "<number>"    → type: "cutoff_time",  cutoffMinutesFromMidnight: <number>
export type CutoffSelectValue = string;

// { [centerUserId]: { [dateISO]: CutoffSelectValue } }
export type CalendarEditState = Record<
  string,
  Record<string, CutoffSelectValue>
>;

const cutoffToSelectValue = (cutoff: RadiologistCutoff): CutoffSelectValue => {
  if (cutoff.type === "unavailable") return "unavailable";
  if (
    cutoff.type === "cutoff_time" &&
    cutoff.cutoffMinutesFromMidnight !== null
  ) {
    return String(cutoff.cutoffMinutesFromMidnight);
  }
  return "available";
};

const buildInitialState = (
  cutoffs: RadiologistCutoff[],
  radiologistIds: string[],
  dates: string[],
): CalendarEditState => {
  return radiologistIds.reduce<CalendarEditState>((state, userId) => {
    const userDates = dates.reduce<Record<string, CutoffSelectValue>>(
      (acc, date) => {
        const existing = cutoffs.find(
          (c) => c.centerUserId === userId && c.date === date,
        );
        return {
          ...acc,
          [date]: existing ? cutoffToSelectValue(existing) : "available",
        };
      },
      {},
    );
    return { ...state, [userId]: userDates };
  }, {});
};

type UseCalendarEditStateParams = {
  cutoffs: RadiologistCutoff[];
  radiologistIds: string[];
  dates: string[];
};

const useCalendarEditState = ({
  cutoffs,
  radiologistIds,
  dates,
}: UseCalendarEditStateParams) => {
  const [editState, setEditState] = useState<CalendarEditState>(() =>
    buildInitialState(cutoffs, radiologistIds, dates),
  );

  const getValue = useCallback(
    (userId: string, date: string): CutoffSelectValue => {
      return editState[userId]?.[date] ?? "available";
    },
    [editState],
  );

  const setValue = useCallback(
    (userId: string, date: string, value: CutoffSelectValue) => {
      setEditState((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          [date]: value,
        },
      }));
    },
    [],
  );

  const resetToInitial = useCallback(
    (newCutoffs: RadiologistCutoff[]) => {
      setEditState(buildInitialState(newCutoffs, radiologistIds, dates));
    },
    [radiologistIds, dates],
  );

  return { editState, getValue, setValue, resetToInitial };
};

export default useCalendarEditState;
